<template>
  <DToaster />
  <div class="dashboard-layout d-flex">
    <DXDashboardSidebar
      :navigation="$page.props.navigation"
      :current-url="$page.url"
      :collapsed="sidebarCollapsed"
      :title="appName"
      @toggle="toggleSidebar"
    >
      <template #brand="{ collapsed }">
        <h4 v-if="collapsed" class="mb-0 fw-bold brand-text text-center">A</h4>
        <div v-else class="px-2 pt-3">
          <!-- :src binding (not static src): laravel-vite-plugin sets
               publicDir false, so a static src is mis-treated as a module
               import and breaks the build. -->
          <img :src="'/logo.webp'" :alt="appName" class="brand-logo rounded" />
        </div>
      </template>

      <template #link="{ item, isActive, collapsed }">
        <Link
          :href="item.url"
          class="nav-link d-flex align-items-center gap-2 rounded"
          :class="{
            active: isActive,
            'justify-content-center': collapsed,
          }"
        >
          <component
            v-if="item.icon"
            :is="useIcon(item.icon)"
            class="nav-icon"
            style="width: 20px; height: 20px"
          />
          <span v-if="!collapsed" class="nav-label">{{ item.label }}</span>
        </Link>
      </template>
    </DXDashboardSidebar>

    <div class="dashboard-content flex-grow-1">
      <DXDashboardNavbar
        :user="$page.props.auth?.user"
        :page-title="$page.props.pageTitle"
        @toggle-sidebar="toggleSidebar"
      >
        <template #menu-icon>
          <i-bi-list style="width: 24px; height: 24px" />
        </template>
        <template #user-menu-items>
          <DDropdownItem>
            <Link
              href="/logout"
              method="post"
              as="button"
              class="btn btn-link text-decoration-none text-start w-100 p-0 text-dark"
            >
              <i-bi-box-arrow-right
                class="me-2"
                style="width: 16px; height: 16px"
              />
              Logout
            </Link>
          </DDropdownItem>
        </template>
      </DXDashboardNavbar>

      <main class="dashboard-main p-4">
        <slot />
      </main>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from "vue";
import { Link, usePage } from "@inertiajs/vue3";
import {
  DXDashboardSidebar,
  DXDashboardNavbar,
  DDropdownItem,
  DToaster,
  useToast,
} from "@omnitend/dashboard-for-laravel";
import { useIcon } from "../composables/useIcon";

const appName = "AstroAdmin Cloud";
const sidebarCollapsed = ref(false);
const page = usePage();
const toast = useToast();

const toggleSidebar = () => {
  sidebarCollapsed.value = !sidebarCollapsed.value;
};

// Watch for flash messages and show toasts
watch(
  () => page.props.flash,
  (flash: any) => {
    if (flash?.success) {
      toast.show({
        props: {
          title: "Success",
          body: flash.success,
          variant: "success",
        },
      });
    }
    if (flash?.error) {
      toast.show({
        props: {
          title: "Error",
          body: flash.error,
          variant: "danger",
        },
      });
    }
    if (flash?.info) {
      toast.show({
        props: {
          title: "Info",
          body: flash.info,
          variant: "info",
        },
      });
    }
    if (flash?.warning) {
      toast.show({
        props: {
          title: "Warning",
          body: flash.warning,
          variant: "warning",
        },
      });
    }
  },
  { deep: true },
);
</script>

<style scoped>
.dashboard-layout {
  min-height: 100vh;
  background-color: #f8f9fa;
}

.dashboard-content {
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
}

.dashboard-main {
  flex: 1;
  max-width: 100%;
}

.brand-text {
  color: #5b8def;
}

.brand-logo {
  display: block;
  width: 88%;
  height: auto;
  margin: 0 auto;
}
</style>
